import type { Lang } from '../../i18n';

/**
 * Privacy policy (/privacy) and contact page (/contact). Long prose, so it lives here rather than
 * in the UI dictionary. Keep the policy in step with what the code does (data kept, who sees it,
 * deletion) — Strava, Google and Facebook reviewers read it.
 */

export const CONTACT = {
  email: 'support@runnerwars.com',
  phone: '+84708068069',
  phoneDisplay: '+84 708 068 069',
  site: 'https://www.runnerwars.com',
};

/** Date the policy last changed (shown on the page). */
export const POLICY_UPDATED = '2026-10-14';

export interface LegalSection {
  id: string;
  title: string;
  text?: string;
  bullets?: string[];
}

export interface LegalCopy {
  back: string;
  privacyTitle: string;
  updated: string;
  intro: string;
  sections: LegalSection[];
  contactTitle: string;
  contactIntro: string;
  operatorLabel: string;
  operator: string;
  address: string;
  supportTitle: string;
  supportText: string;
  supportButton: string;
  supportSubject: string;
  deleteTitle: string;
  deleteText: string;
  deleteButton: string;
  deleteSubject: string;
  deleteInApp: string;
  emailLabel: string;
  phoneLabel: string;
  addressLabel: string;
  includeTitle: string;
  include: string[];
  never: string;
  topics: string[];
  privacyLink: string;
  contactLink: string;
}

const vi: LegalCopy = {
  back: '‹ Quay lại',
  privacyTitle: 'Chính sách quyền riêng tư',
  updated: 'Cập nhật lần cuối',
  intro:
    'Runner Wars là game chạy bộ miễn phí cho runner ở Đà Nẵng. Chính sách này giải thích dữ liệu nào chúng tôi thu thập, dùng để làm gì, ai nhìn thấy, lưu ở đâu, bao lâu, và cách bạn xem, sửa hoặc xoá dữ liệu của mình.',
  sections: [
    {
      id: 'van-hanh',
      title: '1. Ai vận hành Runner Wars',
      text: 'Runner Wars là game do đội ngũ Runner Wars (Đà Nẵng, Việt Nam) phát triển và vận hành. Liên hệ: support@runnerwars.com — xem trang Liên hệ.',
    },
    {
      id: 'du-lieu',
      title: '2. Dữ liệu chúng tôi thu thập',
      bullets: [
        'Tài khoản: tên hiển thị, email, mật khẩu (chỉ lưu dạng băm bcrypt, không ai đọc được), ảnh đại diện, màu trên bản đồ, ngôn ngữ.',
        'Đăng nhập bằng Strava, Google hoặc Facebook: mã định danh tài khoản bên đó, tên và ảnh đại diện; email nếu bên đó cung cấp.',
        'Hồ sơ (tuỳ chọn): lời giới thiệu và các thông tin cá nhân khác bạn tự nhập trong Sửa hồ sơ.',
        'Buổi chạy: các điểm GPS (vị trí, thời gian, độ chính xác, độ cao), quãng đường, thời gian, pace, tên thiết bị; file GPX/TCX/FIT bạn tải lên.',
        'Strava (nếu bạn kết nối): mã vận động viên, token truy cập (mã hoá), và các buổi chạy/đi bộ của chính bạn bắt đầu sau khi kết nối, trong 7 ngày gần nhất — thông tin tóm tắt và luồng vị trí, thời gian, độ cao.',
        'Dữ liệu game: ô chiếm được, XP, cấp độ, bậc hạng, huy hiệu, club, sự kiện, bình luận, kudos, thông báo.',
        'Kỹ thuật: đăng ký nhận thông báo đẩy (Web Push) của thiết bị, cài đặt thông báo, và nhật ký máy chủ (như địa chỉ IP, thời điểm truy cập) để vận hành và bảo mật.',
        'Ban tổ chức sự kiện dùng Zalo bot: token bot (mã hoá) và mã nhóm Zalo nhận thông báo.',
      ],
    },
    {
      id: 'muc-dich',
      title: '3. Chúng tôi dùng dữ liệu để làm gì',
      bullets: [
        'Vận hành game: tính quãng đường, pace, ô chiếm được, XP, bậc hạng, huy hiệu và bảng xếp hạng — mọi kết quả do máy chủ tính từ dữ liệu GPS.',
        'Kiểm tra buổi chạy hợp lệ (chống gian lận: tốc độ phương tiện, GPS bất thường, trùng lặp).',
        'Tính điểm sự kiện, club và đội; đăng kết quả sự kiện vào nhóm Zalo của sự kiện (nếu ban tổ chức bật).',
        'Gửi thông báo trong app và thông báo đẩy mà bạn bật (tắt được từng loại trong Hồ sơ).',
        'Hỗ trợ khi bạn liên hệ, bảo mật hệ thống và xử lý vi phạm.',
      ],
    },
    {
      id: 'cong-khai',
      title: '4. Ai nhìn thấy dữ liệu của bạn',
      bullets: [
        'Người chơi khác thấy: tên hiển thị, ảnh, màu, giới thiệu, cấp độ, bậc hạng, huy hiệu, thống kê, club, các buổi chạy hợp lệ kèm bản đồ tuyến chạy, ô bạn đang giữ, kết quả sự kiện, bình luận và kudos.',
        'Bật "Ẩn đoạn đầu và cuối lộ trình" để xoá 200 m đầu và cuối tuyến chạy với mọi người trừ bạn.',
        'Chỉ bạn thấy: email, thông tin cá nhân trong hồ sơ (trừ lời giới thiệu) và token kết nối.',
        'Cập nhật trực tiếp (realtime) chỉ gửi ô lục giác ~200 m, không bao giờ gửi vị trí GPS trực tiếp của bạn.',
        'Nếu bạn tham gia sự kiện có Zalo bot, tin nhắn trong nhóm Zalo gồm tên hiển thị, đội, quãng đường, thời gian, pace, số ô và thứ hạng của bạn.',
        'Quản trị viên game chỉ xem dữ liệu khi cần hỗ trợ hoặc xử lý vi phạm; mọi thao tác quản trị đều được ghi nhật ký.',
      ],
    },
    {
      id: 'strava',
      title: '5. Kết nối Strava',
      bullets: [
        'Kết nối là tuỳ chọn, qua nút "Connect with Strava" chính thức (OAuth); chúng tôi không bao giờ thấy mật khẩu Strava của bạn.',
        'Chỉ nhập buổi chạy/đi bộ của chính bạn bắt đầu sau khi kết nối và trong 7 ngày gần nhất; lịch sử cũ không bao giờ được nhập.',
        'Ngắt kết nối trong app hoặc thu hồi quyền trên Strava sẽ xoá token ngay lập tức. Buổi chạy bị xoá trên Strava thì tuyến GPS đã nhập cũng bị xoá.',
        'Chúng tôi không bán, không chia sẻ dữ liệu Strava cho bên thứ ba, không dùng cho quảng cáo hay huấn luyện AI/ML.',
      ],
    },
    {
      id: 'khong-lam',
      title: '6. Những điều chúng tôi không làm',
      bullets: [
        'Không bán hay cho thuê dữ liệu cá nhân.',
        'Không hiển thị quảng cáo, không dùng công cụ theo dõi hay phân tích của bên thứ ba.',
        'Không dùng dữ liệu của bạn để huấn luyện AI/ML.',
        'App chỉ lưu trên thiết bị của bạn (localStorage) phiên đăng nhập, ngôn ngữ, cài đặt và buổi chạy chưa đồng bộ — không dùng cookie quảng cáo.',
      ],
    },
    {
      id: 'ben-thu-ba',
      title: '7. Dịch vụ bên thứ ba',
      bullets: [
        'Railway (máy chủ và cơ sở dữ liệu, đặt tại Singapore).',
        'Strava, Google, Facebook — khi bạn chọn đăng nhập hoặc kết nối bằng các dịch vụ này.',
        'Mapbox hoặc CARTO (ảnh nền bản đồ) — nhận địa chỉ IP và vùng bản đồ bạn đang xem.',
        'Dịch vụ thông báo đẩy của trình duyệt (Apple, Google, Mozilla) — chuyển thông báo tới thiết bị của bạn.',
        'Zalo — chỉ với sự kiện mà ban tổ chức bật Zalo bot.',
        'ImprovMX — chuyển tiếp email gửi tới support@runnerwars.com.',
      ],
      text: 'Mỗi dịch vụ có chính sách riêng. Dữ liệu của bạn được lưu trữ và xử lý trên máy chủ tại Singapore.',
    },
    {
      id: 'bao-mat',
      title: '8. Bảo mật',
      bullets: [
        'Mật khẩu được băm bằng bcrypt; token Strava và Zalo được mã hoá AES-256-GCM.',
        'Cơ sở dữ liệu chỉ truy cập được trong mạng nội bộ của máy chủ, không mở ra Internet.',
        'Mọi kết nối dùng HTTPS.',
      ],
    },
    {
      id: 'luu-tru',
      title: '9. Thời gian lưu trữ',
      bullets: [
        'Dữ liệu được giữ khi tài khoản của bạn còn hoạt động.',
        'Xoá một buổi chạy: buổi chạy, tuyến GPS và mọi thứ nó mang lại (XP, ô, điểm sự kiện) bị xoá ngay.',
        'Xoá tài khoản: toàn bộ dữ liệu cá nhân bị xoá ngay — hồ sơ, buổi chạy, GPS, XP, bình luận, kudos, thông báo, kết nối. Ô bạn giữ trở về trung lập; club và sự kiện bạn sở hữu được chuyển cho thành viên khác.',
        'Để chống lạm dụng, nhật ký quản trị giữ lại một dòng ghi nhận việc xoá (email, thời điểm, số lượng dữ liệu đã xoá).',
        'Tin nhắn đã gửi vào nhóm Zalo của sự kiện nằm trên Zalo, chúng tôi không xoá được.',
        'Chỉ giữ 100 thông báo gần nhất của mỗi người; nhật ký máy chủ được nhà cung cấp lưu trong thời gian giới hạn.',
      ],
    },
    {
      id: 'quyen',
      title: '10. Quyền của bạn',
      bullets: [
        'Xem và sửa hồ sơ bất cứ lúc nào trong Hồ sơ → Sửa hồ sơ.',
        'Tắt từng loại thông báo, ẩn điểm đầu/cuối tuyến chạy, ngắt kết nối Strava.',
        'Xoá từng buổi chạy, hoặc tự xoá tài khoản trong Hồ sơ → Xóa tài khoản.',
        'Yêu cầu bản sao dữ liệu, chỉnh sửa hoặc xoá dữ liệu qua email support@runnerwars.com — chúng tôi xử lý theo quy định pháp luật Việt Nam về bảo vệ dữ liệu cá nhân.',
      ],
    },
    {
      id: 'thay-doi',
      title: '11. Thay đổi chính sách',
      text: 'Khi chính sách thay đổi, chúng tôi cập nhật ngày ở đầu trang và thông báo trong app nếu thay đổi quan trọng.',
    },
  ],
  contactTitle: 'Liên hệ',
  contactIntro:
    'Hỗ trợ tài khoản, kết nối Strava, buổi chạy, GPS và bản đồ, sự kiện, bảng xếp hạng, club — và mọi yêu cầu về dữ liệu cá nhân.',
  operatorLabel: 'Đơn vị vận hành',
  operator: 'Runner Wars',
  address: '126 Trịnh Lỗi, Ngũ Hành Sơn, Đà Nẵng, Việt Nam',
  supportTitle: 'Hỗ trợ',
  supportText: 'Gặp lỗi hoặc cần giúp đỡ? Gửi email, chúng tôi sẽ trả lời sớm nhất có thể.',
  supportButton: '✉️ Gửi email hỗ trợ',
  supportSubject: 'Runner Wars - Hỗ trợ',
  deleteTitle: 'Quyền riêng tư & xoá dữ liệu',
  deleteText:
    'Muốn nhận bản sao dữ liệu, sửa hoặc xoá dữ liệu cá nhân? Gửi yêu cầu từ email của tài khoản để chúng tôi xác minh.',
  deleteButton: '🗑️ Yêu cầu xoá dữ liệu',
  deleteSubject: 'Runner Wars - Yêu cầu xoá dữ liệu',
  deleteInApp: 'Bạn cũng có thể tự xoá tài khoản ngay trong app: Hồ sơ → Xóa tài khoản.',
  emailLabel: 'Email',
  phoneLabel: 'Điện thoại',
  addressLabel: 'Địa chỉ',
  includeTitle: 'Khi liên hệ, vui lòng ghi rõ',
  include: [
    'Tên hiển thị trong game và email tài khoản',
    'Link buổi chạy hoặc sự kiện liên quan (nếu có)',
    'Mô tả lỗi, kèm ảnh chụp màn hình nếu được',
  ],
  never:
    'Không bao giờ gửi mật khẩu, mã OTP hay token Strava qua email — chúng tôi không bao giờ hỏi những thông tin này.',
  topics: [
    'Tài khoản',
    'Strava',
    'Buổi chạy',
    'GPS & bản đồ',
    'Sự kiện',
    'Bảng xếp hạng',
    'Club',
    'Dữ liệu cá nhân',
  ],
  privacyLink: 'Chính sách quyền riêng tư',
  contactLink: 'Liên hệ',
};

const en: LegalCopy = {
  back: '‹ Back',
  privacyTitle: 'Privacy Policy',
  updated: 'Last updated',
  intro:
    'Runner Wars is a free running game for runners in Đà Nẵng. This policy explains what data we collect, what we use it for, who can see it, where and how long it is kept, and how you can view, correct or delete it.',
  sections: [
    {
      id: 'operator',
      title: '1. Who runs Runner Wars',
      text: 'Runner Wars is developed and operated by the Runner Wars team (Đà Nẵng, Vietnam). Contact: support@runnerwars.com — see the Contact page.',
    },
    {
      id: 'data',
      title: '2. Data we collect',
      bullets: [
        'Account: display name, email, password (stored only as a bcrypt hash that nobody can read), profile photo, map colour, language.',
        'Sign-in with Strava, Google or Facebook: that account’s identifier, name and photo; the email if the provider shares it.',
        'Profile (optional): your bio and any other personal details you enter in Edit profile.',
        'Runs: GPS points (position, time, accuracy, altitude), distance, duration, pace, device name; GPX/TCX/FIT files you upload.',
        'Strava (if you connect it): athlete id, access tokens (encrypted), and your own run/walk activities that started after you connected, within the last 7 days — the summary and the position, time and altitude streams.',
        'Game data: captured sectors, XP, level, rank, badges, clubs, events, comments, kudos, notifications.',
        'Technical: your device’s Web Push subscription, notification settings, and server logs (such as IP address and time of access) for operations and security.',
        'Event organisers using a Zalo bot: the bot token (encrypted) and the Zalo group id it posts to.',
      ],
    },
    {
      id: 'use',
      title: '3. How we use it',
      bullets: [
        'To run the game: distance, pace, captured sectors, XP, ranks, badges and leaderboards — all computed by our server from GPS data.',
        'To check that runs are valid (anti-cheat: vehicle speed, implausible GPS, duplicates).',
        'To score events, clubs and teams, and to post event results to the event’s Zalo group when the organisers enable it.',
        'To send in-app notifications and the push notifications you turn on (each type can be switched off in Profile).',
        'To answer your support requests, keep the service secure and handle abuse.',
      ],
    },
    {
      id: 'visibility',
      title: '4. Who can see your data',
      bullets: [
        'Other players see: display name, photo, colour, bio, level, rank, badges, stats, club, your valid runs with their route map, the sectors you hold, event results, comments and kudos.',
        'Turn on "Hide start & end of my routes" to remove the first and last 200 m of your routes for everyone but you.',
        'Only you see: your email, the personal details in your profile (except your bio) and connection tokens.',
        'Realtime updates only share ~200 m hexagonal sectors, never your live GPS position.',
        'If you join an event with a Zalo bot, messages in its Zalo group include your display name, team, distance, time, pace, sectors and rank.',
        'The game administrator looks at data only for support or to handle abuse; every admin action is logged.',
      ],
    },
    {
      id: 'strava',
      title: '5. Strava',
      bullets: [
        'Connecting is optional and uses the official "Connect with Strava" button (OAuth); we never see your Strava password.',
        'We only import your own run/walk activities that started after you connected and within the last 7 days; older history is never imported.',
        'Disconnecting in the app or revoking access on Strava deletes the tokens immediately. If you delete an activity on Strava, its imported GPS route is deleted too.',
        'We do not sell or share Strava data with third parties, and do not use it for advertising or AI/ML training.',
      ],
    },
    {
      id: 'never',
      title: '6. What we don’t do',
      bullets: [
        'We don’t sell or rent personal data.',
        'We show no ads and use no third-party tracking or analytics tools.',
        'We don’t use your data to train AI/ML models.',
        'On your device the app only stores (in localStorage) your sign-in session, language, settings and runs not yet synced — no advertising cookies.',
      ],
    },
    {
      id: 'third-parties',
      title: '7. Third-party services',
      bullets: [
        'Railway (servers and database, located in Singapore).',
        'Strava, Google, Facebook — when you choose to sign in or connect with them.',
        'Mapbox or CARTO (map tiles) — receive your IP address and the map area you view.',
        'Browser push services (Apple, Google, Mozilla) — deliver notifications to your device.',
        'Zalo — only for events whose organisers enable a Zalo bot.',
        'ImprovMX — forwards email sent to support@runnerwars.com.',
      ],
      text: 'Each has its own policy. Your data is stored and processed on servers in Singapore.',
    },
    {
      id: 'security',
      title: '8. Security',
      bullets: [
        'Passwords are hashed with bcrypt; Strava and Zalo tokens are encrypted with AES-256-GCM.',
        'The database is reachable only on the host’s private network, never from the Internet.',
        'All connections use HTTPS.',
      ],
    },
    {
      id: 'retention',
      title: '9. Retention',
      bullets: [
        'Data is kept while your account is active.',
        'Deleting a run removes the run, its GPS route and everything it earned (XP, sectors, event points) immediately.',
        'Deleting your account removes all your personal data immediately — profile, runs, GPS, XP, comments, kudos, notifications, connections. Your sectors return to neutral; clubs and events you own pass to other members.',
        'To prevent abuse, the admin log keeps one record of the deletion (email, time, amount of data removed).',
        'Messages already posted to an event’s Zalo group live on Zalo and cannot be deleted by us.',
        'Only your latest 100 notifications are kept; server logs are kept by our host for a limited time.',
      ],
    },
    {
      id: 'rights',
      title: '10. Your rights',
      bullets: [
        'View and edit your profile at any time in Profile → Edit profile.',
        'Switch off any notification type, hide the start and end of your routes, disconnect Strava.',
        'Delete single runs, or delete your account yourself in Profile → Delete account.',
        'Ask for a copy of your data, a correction or deletion at support@runnerwars.com — we handle requests under Vietnam’s personal data protection law.',
      ],
    },
    {
      id: 'changes',
      title: '11. Changes',
      text: 'When this policy changes we update the date at the top, and announce important changes in the app.',
    },
  ],
  contactTitle: 'Contact',
  contactIntro:
    'Help with your account, Strava, runs, GPS and maps, events, leaderboards, clubs — and any personal data request.',
  operatorLabel: 'Operated by',
  operator: 'Runner Wars',
  address: '126 Trịnh Lỗi, Ngũ Hành Sơn, Đà Nẵng, Vietnam',
  supportTitle: 'Support',
  supportText: 'Found a bug or need help? Email us and we’ll reply as soon as we can.',
  supportButton: '✉️ Email support',
  supportSubject: 'Runner Wars - Support',
  deleteTitle: 'Privacy & data deletion',
  deleteText:
    'Want a copy of your data, a correction, or your personal data deleted? Send the request from your account’s email so we can verify it.',
  deleteButton: '🗑️ Request data deletion',
  deleteSubject: 'Runner Wars - Data deletion request',
  deleteInApp: 'You can also delete your account yourself in the app: Profile → Delete account.',
  emailLabel: 'Email',
  phoneLabel: 'Phone',
  addressLabel: 'Address',
  includeTitle: 'When you write, please include',
  include: [
    'Your display name and account email',
    'A link to the run or event concerned (if any)',
    'What went wrong, with a screenshot if possible',
  ],
  never:
    'Never send your password, OTP codes or Strava tokens by email — we will never ask for them.',
  topics: [
    'Account',
    'Strava',
    'Runs',
    'GPS & maps',
    'Events',
    'Leaderboards',
    'Clubs',
    'Personal data',
  ],
  privacyLink: 'Privacy Policy',
  contactLink: 'Contact',
};

export const LEGAL: Record<Lang, LegalCopy> = { vi, en };
